import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { FormField } from '@/features/shared/form-field';
import { householdRoleLabel } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

export function HouseholdsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const query = useQuery({
    queryKey: queryKeys.households.all,
    queryFn: () => api.households.list(),
  });

  const create = useMutation({
    mutationFn: () => api.households.create({ name: name.trim() }),
    onSuccess: (h) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
      setCreateOpen(false);
      setName('');
      navigate(`/households/${h.id}`);
    },
    onError: (e) => toastError(e),
  });

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('households.title')}
        description={t('households.description')}
        actions={
          <Button onClick={() => setCreateOpen(true)} data-testid="household-create">
            <Plus aria-hidden /> {t('households.create')}
          </Button>
        }
      />
      {query.isLoading && <Spinner label={t('app.loading')} />}
      {query.error && <Alert variant="destructive">{describeError(query.error)}</Alert>}
      {query.data && query.data.length === 0 && (
        <EmptyState
          illustration={<Illustration name="households" />}
          title={t('households.emptyTitle')}
          description={t('households.emptyBody')}
          action={<Button onClick={() => setCreateOpen(true)}>{t('households.create')}</Button>}
        />
      )}
      <ul className="space-y-2">
        {(query.data ?? []).map((h) => (
          <li key={h.id}>
            <Card>
              <CardContent className="p-3 md:p-4">
                <Link
                  to={`/households/${h.id}`}
                  className="flex items-center gap-3 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="household-link"
                >
                  <Users className="size-6 text-primary" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{h.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t('households.memberCount', { count: h.memberCount })}
                    </span>
                  </span>
                  <Badge variant="outline">{householdRoleLabel(t, h.myRole)}</Badge>
                </Link>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('households.create')}</DialogTitle>
            <DialogDescription>{t('households.createDescription')}</DialogDescription>
          </DialogHeader>
          <form
            noValidate
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <FormField label={t('households.fields.name')} required>
              <Input
                value={name}
                maxLength={120}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                data-testid="household-name"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={create.isPending}
                disabled={!name.trim()}
                data-testid="household-save"
              >
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
